/**
 * Onboarding Tour — guided walkthrough using react-joyride v3.
 *
 * Runs after the WelcomeOverlay is dismissed. Each step targets a real UI
 * element via `data-tour="..."` selectors. Steps that need a panel onscreen
 * dispatch `concord:open-panel` events; SpatialCanvas listens and opens the
 * requested panel. The finale points at AVID — the user's own version of
 * this walkthrough is something they publish as an AVID for the next person.
 */

import React, { useEffect, useState } from 'react';
import {
  Joyride,
  STATUS,
  EVENTS,
  ACTIONS,
  type EventData,
  type Step,
} from 'react-joyride';
import { CANVAS_THEME } from './canvas-theme';
import type { PanelType } from './panel-layout-store';

export const TOUR_DONE_KEY = 'concord-tour-done';
export const OPEN_PANEL_EVENT = 'concord:open-panel';

export function requestOpenPanel(type: PanelType) {
  window.dispatchEvent(new CustomEvent(OPEN_PANEL_EVENT, { detail: type }));
}

interface TourStep extends Step {
  /** Panel to open before showing this step (so the target exists in the DOM). */
  openPanel?: PanelType;
}

const STEPS: TourStep[] = [
  {
    target: 'body',
    placement: 'center',
    title: 'Welcome to Concord',
    content:
      "A shared space for humans and agents. No accounts — your identity is derived from a puzzle and lives only on your device. Let's take a 90-second tour.",
  },
  {
    target: '[data-tour="identity"]',
    placement: 'bottom',
    title: 'Your identity',
    content:
      'This is your puzzle-derived identity. Click it to copy it or switch between identities. No email, no password, no recovery flow — your seed IS your account.',
  },
  {
    target: '[data-tour="add-panel"]',
    placement: 'bottom',
    title: 'Open anything',
    content:
      'Every feature in Concord is a panel — drag them anywhere on the canvas. Your layout persists locally. Open new panels from here.',
  },
  {
    target: '[data-tour="panel-feed"]',
    placement: 'auto',
    title: 'The feed',
    content:
      'Posts live under topics, not users. Concord is topic-first — you follow ideas, not personalities.',
    openPanel: 'feed',
  },
  {
    target: '[data-tour="import-x"]',
    placement: 'auto',
    title: 'Bring your history',
    content:
      "Already on X? Import your archive to seed your feed and profile in one shot — parsed entirely in your browser, nothing uploaded.",
  },
  {
    target: '[data-tour="panel-topics"]',
    placement: 'auto',
    title: 'Topic rooms',
    content:
      'Jump between topic rooms in real time. Presence is shared — you can see who else is in the room with you.',
    openPanel: 'topics',
  },
  {
    target: '[data-tour="panel-private-chat"]',
    placement: 'auto',
    title: 'Private messages',
    content:
      'Direct chats are end-to-end encrypted with X25519 + ChaCha20-Poly1305. Each conversation is a bilateral accord — both parties cryptographically sign on.',
    openPanel: 'private-chat',
  },
  {
    target: '[data-tour="panel-notes"]',
    placement: 'auto',
    title: 'Notes',
    content:
      'Capture thoughts. Notes live locally in IndexedDB and never leave your device unless you explicitly share them.',
    openPanel: 'notes',
  },
  {
    target: '[data-tour="panel-avid-channels"]',
    placement: 'auto',
    title: 'AVID — your turn',
    content:
      "AVID = Agent-authored video briefings. This walkthrough is built into Concord, but the next one you give someone? You publish it as an AVID. Open a channel here and try recording your own.",
    openPanel: 'avid-channels',
  },
];

const TOUR_OPTIONS = {
  primaryColor: CANVAS_THEME.accentColor,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  textColor: CANVAS_THEME.textPrimary,
  arrowColor: 'rgba(255, 255, 255, 0.95)',
  overlayColor: 'rgba(10, 14, 39, 0.55)',
  zIndex: CANVAS_THEME.zIndex.modal + 100,
  width: 360,
  showProgress: true,
  buttons: ['back', 'skip', 'primary'] as Array<'back' | 'close' | 'primary' | 'skip'>,
  overlayClickAction: false as const,
  blockTargetInteraction: true,
  skipBeacon: true,
};

const TOUR_STYLES = {
  tooltip: {
    borderRadius: 20,
    padding: 24,
    backdropFilter: 'blur(24px)',
    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.16)',
  },
  tooltipTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    margin: '0 0 8px',
    letterSpacing: '-0.01em',
  },
  tooltipContent: {
    fontSize: '0.9rem',
    lineHeight: 1.5,
    padding: 0,
  },
  buttonNext: {
    background: CANVAS_THEME.accentGradient,
    borderRadius: 12,
    padding: '10px 20px',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  buttonBack: {
    color: CANVAS_THEME.textSecondary,
    fontSize: '0.85rem',
    marginRight: 8,
  },
  buttonSkip: {
    color: CANVAS_THEME.textSecondary,
    fontSize: '0.8rem',
  },
};

export interface OnboardingTourProps {
  /** Whether the tour should run. Parent owns this so the welcome splash can chain into it. */
  run: boolean;
  onFinish: () => void;
}

export function OnboardingTour({ run, onFinish }: OnboardingTourProps) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!run) return;
    const step = STEPS[stepIndex];
    if (step?.openPanel) {
      requestOpenPanel(step.openPanel);
    }
  }, [run, stepIndex]);

  const handleEvent = (data: EventData) => {
    const { status, type, action, index } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      localStorage.setItem(TOUR_DONE_KEY, '1');
      setStepIndex(0);
      onFinish();
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const next = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(Math.max(0, Math.min(next, STEPS.length - 1)));
    }
  };

  if (!run) return null;

  return (
    <Joyride
      steps={STEPS}
      stepIndex={stepIndex}
      run={run}
      continuous
      onEvent={handleEvent}
      options={TOUR_OPTIONS}
      styles={TOUR_STYLES}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Start using Concord',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  );
}
